import { useLayoutEffect, useRef, type InputHTMLAttributes } from "react";
import { formatAmount } from "../payrollPresentation";

/**
 * 금액 한 칸. 예전에는 눌러서 숫자를 찍는 키패드가 따라 붙었는데, 이 팝업은 책상에서
 * 키보드로 채우는 화면이라 칸을 누를 때마다 화면 절반이 가려졌다. 평범한 숫자 입력으로
 * 두고, 천 단위 쉼표만 입력하는 동안 붙여 준다 — 커서 자리는 숫자 개수로 세어 되돌린다.
 *
 * "원"은 붙이지 않는다. 이 칸을 쓰는 곳의 이름표가 전부 돈의 이름(단가·시급·정산 금액·
 * 공제·추가 지급)이라 단위를 한 번 더 말할 뿐이고, 칸 안에 글자가 하나 떠 있으면 숫자가
 * 쓸 자리가 그만큼 줄어든다.
 */
export function PayrollAmountInput({ value, onValue, label, testId, disabled, sign, ...props }: Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & { value: string; onValue: (value: string) => void; label: string; testId: string; sign?: "minus" | "plus" }) {
  const input = useRef<HTMLInputElement>(null), caret = useRef<number | null>(null);
  useLayoutEffect(() => {
    if (caret.current === null || !input.current) return;
    const digitCount = caret.current; caret.current = null; let index = 0, digits = 0;
    while (index < value.length && digits < digitCount) { if (value[index] !== ",") digits++; index++; }
    input.current.setSelectionRange(index, index);
  }, [value]);
  return <div className={`payroll-money-control${sign ? ` payroll-money-control--${sign}` : ""}`}>
    {/* 공제는 빼는 돈이다 — 칸에 적는 숫자는 양수지만 장부에 가면 음수라, 치는 동안 그
        사실이 보이지 않으면 "10000을 적었는데 -10000이 됐다"가 된다. 부호를 앞에 세운다. */}
    {sign && <span aria-hidden="true">{sign === "minus" ? "−" : "+"}</span>}
    <input {...props} ref={input} className="ui-input" type="text" inputMode="numeric" autoComplete="off"
      value={value} disabled={disabled} aria-label={label} data-testid={testId}
      onChange={event => {
        caret.current = event.target.value.slice(0, event.target.selectionStart ?? event.target.value.length).replaceAll(",", "").length;
        onValue(formatAmount(event.target.value));
      }} />
  </div>;
}
